// Phase 15 · Portal Screen 2 — Beat → Outlet, the distributor's own
// equivalent of the SE's "Today's Beat": pick one of their mapped beats,
// see Total/Pending/Visited tabs for it (same as SE/ASM), and either punch
// a secondary order (reuses the existing SecondaryOrder screen) or close
// the call as non-productive with a reason — same pattern the field app
// already uses, minus the selfie machinery a desk-based distributor
// doesn't have.
//
// A distributor has no PJP (there's nothing to "plan" — they aren't
// assigned one fixed beat, they pick one of their mapped distributor(s)'
// beats each visit from a dropdown), so this reuses GET /beats/today with
// an explicit `beat_id` instead of the PJP-driven lookup SE/ASM get for
// free (see backend/routes_phase29_2.py).
//
// Whether GPS is captured on a visit is an ADMIN decision, not a per-call
// toggle the distributor flips themselves — same lever already used for
// SE/ASM (the READ_GEO_LOCATION workflow permission on Workflow Permissions
// admin). If admin grants it to a distributor's login, GPS is captured
// silently here; if not, an empty gps is sent (visit_start already accepts
// that for role=DISTRIBUTOR — see backend/routes_visits.py).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, ShoppingCart, Ban, CheckCircle2, Info, PlusCircle, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import RetailerDetailsSheet from "@/components/RetailerDetailsSheet";
import OutletColorChip from "@/components/outlets/OutletColorChip";
import OutletColorLegend from "@/components/outlets/OutletColorLegend";
import {
  activeColourIn, cleanLegend, cleanTag, countByColour, matchesColour,
} from "@/lib/outletColors";

const TABS = [
  { key: "total", label: "Total" },
  { key: "pending", label: "Pending" },
  { key: "visited", label: "Visited" },
];

function captureGps() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude,
                        accuracy_m: Math.round(p.coords.accuracy || 0) }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 5000 },
    );
  });
}

// Beat.jsx remounts fresh every time you navigate back here (e.g. after
// placing an order via SecondaryOrder.jsx, a different route entirely) —
// without this, the beat dropdown reset to "Choose a beat" and made the
// distributor reselect it after every single order. Persist per-user so a
// shared device logging in as a different distributor doesn't inherit the
// wrong beat.
function storageKey(userId) { return `tulsi:portal_beat_id:${userId || "anon"}`; }
function readStoredBeatId(userId) {
  try { return localStorage.getItem(storageKey(userId)) || ""; } catch { return ""; }
}
function writeStoredBeatId(userId, beatId) {
  try {
    if (beatId) localStorage.setItem(storageKey(userId), beatId);
    else localStorage.removeItem(storageKey(userId));
  } catch { /* ignore */ }
}

export default function PortalBeat() {
  const nav = useNavigate();
  const location = useLocation();
  const { user, hasWorkflowPerm } = useAuth();
  const gpsOn = hasWorkflowPerm("READ_GEO_LOCATION");
  const [beats, setBeats] = useState([]);
  const [beatId, setBeatIdState] = useState(() => readStoredBeatId(user?.id));
  const setBeatId = (id) => { setBeatIdState(id); writeStoredBeatId(user?.id, id); };
  const [loadingBeats, setLoadingBeats] = useState(true);
  const [data, setData] = useState(null);
  const [loadingToday, setLoadingToday] = useState(false);
  const [tab, setTab] = useState("total");
  const [starting, setStarting] = useState({});   // retailer_id -> bool
  const [npFor, setNpFor] = useState(null);        // retailer being closed non-productive
  const [detailsId, setDetailsId] = useState(null); // retailer_id whose quick-details sheet is open
  const [colourFilter, setColourFilter] = useState(null); // outlet colour-guide filter

  useEffect(() => {
    api.get("/beats", { params: { page_size: 200 } })
      .then(({ data }) => {
        const items = data.items || [];
        setBeats(items);
        if (items.length === 1) {
          setBeatId(items[0].id);
        } else if (beatId && !items.some((b) => b.id === beatId)) {
          // Stored beat is no longer one of ours (remapped/deactivated) —
          // don't get stuck pointing at a dead selection.
          setBeatId("");
        }
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingBeats(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadToday = useCallback(async () => {
    if (!beatId) { setData(null); return; }
    setLoadingToday(true);
    try {
      const { data: res } = await api.get("/beats/today", { params: { beat_id: beatId } });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingToday(false);
    }
  }, [beatId]);

  // Initial load + refetch on return to this route (e.g. after placing an
  // order or closing a non-productive call) — same pattern TodayBeat.jsx
  // uses so Pending/Visited move the outlet the moment its call is done.
  useEffect(() => { loadToday(); }, [loadToday, location.key]);

  const counts = data?.counts || { total: 0, visited: 0, pending: 0 };
  // Outlet colour coding — same as TodayBeat.jsx: /beats/today carries each
  // row's colour + the colour guide; an empty guide means it's off for us.
  const legend = useMemo(() => cleanLegend(data?.color_legend), [data]);
  const tagOf = useCallback(
    (r) => (legend.length ? cleanTag(r.color_tag) : null), [legend]);
  const activeColour = activeColourIn(legend, colourFilter);
  const tabRows = useMemo(() => {
    const all = data?.outlets || [];
    if (tab === "total") return all;
    // "in_progress" (a call started but never finished) counts toward the
    // Pending tab, same as its count on the summary chip — only the row's
    // own rendering (badge + Resume action) tells it apart from a plain
    // never-touched outlet.
    if (tab === "pending") return all.filter((r) => r.status !== "visited");
    return all.filter((r) => r.status === tab);
  }, [data, tab]);
  // Guide counts follow the tab; the colour filter applies on top.
  const colourCounts = useMemo(() => countByColour(tabRows, tagOf), [tabRows, tagOf]);
  const rows = useMemo(
    () => (activeColour ? tabRows.filter((r) => matchesColour(tagOf(r), activeColour)) : tabRows),
    [tabRows, tagOf, activeColour]);

  // Reopen a "Visited" outlet to punch another order (or close it
  // non-productive) the same day — no reason required for the distributor,
  // unlike the SE/ASM reopen flow (see backend/routes_visits.py).
  const [reopening, setReopening] = useState({}); // retailer_id -> bool
  const reopenOutlet = async (outlet, visitId) => {
    if (!visitId) { toast.error("Can't reopen — no earlier visit found for today"); return; }
    setReopening((s) => ({ ...s, [outlet.id]: true }));
    try {
      const gps = gpsOn ? (await captureGps() || {}) : {};
      const { data: visit } = await api.post("/visits/start", {
        retailer_id: outlet.id,
        beat_id: beatId,
        gps,
        is_reopen: true,
        parent_visit_id: visitId,
        client_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.()
                            || `portal-beat-reopen-${outlet.id}-${Date.now()}`),
      });
      // Reopening only makes sense to immediately do something with — land
      // the distributor straight on the order screen for the new visit
      // instead of leaving them stranded back on the Beat list.
      nav(`/field/retailer/${outlet.id}/secondary-order?visit_id=${visit.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReopening((s) => ({ ...s, [outlet.id]: false }));
    }
  };

  const startOrder = async (outlet) => {
    setStarting((s) => ({ ...s, [outlet.id]: true }));
    try {
      const gps = gpsOn ? (await captureGps() || {}) : {};
      const { data: visit } = await api.post("/visits/start", {
        retailer_id: outlet.id,
        beat_id: beatId,
        gps,
        client_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.()
                            || `portal-beat-${outlet.id}-${Date.now()}`),
      });
      nav(`/field/retailer/${outlet.id}/secondary-order?visit_id=${visit.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "visit_already_in_progress") {
        nav(`/field/retailer/${outlet.id}/secondary-order?visit_id=${detail.visit_id}`);
        return;
      }
      toast.error(formatApiError(e));
    } finally {
      setStarting((s) => ({ ...s, [outlet.id]: false }));
    }
  };

  return (
    <div className="p-4 pb-24 space-y-3" data-testid="portal-beat-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Beat</h1>
        <button
          onClick={() => nav(`/outlets/new?from=new-call${beatId ? `&beat_id=${beatId}` : ""}`)}
          data-testid="portal-beat-add-new-call"
          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-[#6D2C91]/30 text-[#6D2C91] text-xs font-medium"
        >
          <PlusCircle className="w-4 h-4" /> New Call
        </button>
      </div>

      {loadingBeats ? (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : beats.length === 0 ? (
        <div className="text-center py-10 text-sm text-neutral-500" data-testid="portal-beat-empty">
          No beats mapped to your account yet.
        </div>
      ) : (
        <Select value={beatId} onValueChange={setBeatId}>
          <SelectTrigger data-testid="portal-beat-select">
            <SelectValue placeholder="Choose a beat" />
          </SelectTrigger>
          <SelectContent>
            {beats.map((b) => (
              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {beatId && (
        <OutletColorLegend
          legend={legend}
          counts={colourCounts}
          active={activeColour}
          onChange={setColourFilter}
          testid="portal-beat-color-legend"
        />
      )}

      {beatId && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`portal-beat-tab-${t.key}`}>
                {t.label} ({counts[t.key] ?? 0})
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-3 space-y-2">
              {loadingToday && (
                <div className="py-6 text-center text-neutral-400">
                  <Loader2 className="h-5 w-5 inline animate-spin" />
                </div>
              )}
              {!loadingToday && rows.length === 0 && (
                <div className="py-10 text-center text-neutral-400 text-sm" data-testid="portal-beat-outlets-empty">
                  {activeColour ? "No outlet with this colour here." : "Nothing here."}
                </div>
              )}
              {rows.map((r) => (
                <OutletRow key={r.outlet.id} row={r}
                           colorTag={tagOf(r)}
                           starting={!!starting[r.outlet.id]}
                           reopening={!!reopening[r.outlet.id]}
                           onOrder={() => startOrder(r.outlet)}
                           onNonProductive={() => setNpFor(r.outlet)}
                           onDetails={() => setDetailsId(r.outlet.id)}
                           onReopen={() => reopenOutlet(r.outlet, r.visit_id)} />
              ))}
            </TabsContent>
          ))}
        </Tabs>
      )}

      {npFor && (
        <NonProductiveDialog
          outlet={npFor}
          beatId={beatId}
          gpsOn={gpsOn}
          onClose={(didClose) => { setNpFor(null); if (didClose) loadToday(); }}
        />
      )}

      <RetailerDetailsSheet
        retailerId={detailsId}
        open={!!detailsId}
        onOpenChange={(v) => !v && setDetailsId(null)}
      />
    </div>
  );
}

function OutletRow({ row, colorTag, starting, reopening, onOrder, onNonProductive, onDetails, onReopen }) {
  const done = row.status === "visited";
  const inProgress = row.status === "in_progress";
  const o = row.outlet;
  return (
    <Card className={"border-neutral-200 rounded-2xl" + (colorTag ? " border-l-4" : "")}
          style={colorTag ? { borderLeftColor: colorTag.color } : undefined}
          data-testid={`portal-beat-outlet-${o.code}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 mt-0.5 text-neutral-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{o.name}</div>
            {colorTag && (
              <div className="mt-0.5">
                <OutletColorChip tag={colorTag} testid={`portal-beat-color-${o.code}`} />
              </div>
            )}
            <div className="text-[11px] text-neutral-500">{o.code}</div>
          </div>
          <button type="button" onClick={onDetails}
                  className="h-7 w-7 shrink-0 flex items-center justify-center rounded-full text-neutral-400 hover:text-[#6D2C91] hover:bg-neutral-100"
                  data-testid={`portal-beat-details-${o.code}`}
                  aria-label="Retailer details">
            <Info className="h-4 w-4" />
          </button>
          {done && (
            <div className="text-green-700 text-[11px] font-semibold flex items-center gap-1 shrink-0"
                 data-testid={`portal-beat-done-${o.code}`}>
              <CheckCircle2 className="h-4 w-4" /> Visited
            </div>
          )}
          {inProgress && (
            <div className="text-amber-700 text-[11px] font-semibold flex items-center gap-1 shrink-0"
                 data-testid={`portal-beat-inprogress-${o.code}`}>
              <Loader2 className="h-4 w-4" /> In Progress
            </div>
          )}
        </div>
        {done && (
          <button onClick={onReopen} disabled={reopening}
                  data-testid={`portal-beat-reopen-btn-${o.code}`}
                  className="w-full h-8 rounded-lg border border-neutral-200 text-neutral-600 text-xs font-medium
                             flex items-center justify-center gap-1.5 disabled:opacity-50">
            {reopening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Reopen call
          </button>
        )}
        {!done && (
          <div className="flex gap-2">
            <button onClick={onOrder} disabled={starting}
                    data-testid={`portal-beat-order-btn-${o.code}`}
                    className="flex-1 h-9 rounded-lg tulsi-gradient text-white text-xs font-medium
                               flex items-center justify-center gap-1.5 disabled:opacity-50">
              {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                         : <ShoppingCart className="w-3.5 h-3.5" />}
              {inProgress ? "Resume order" : "Order"}
            </button>
            <button onClick={onNonProductive}
                    data-testid={`portal-beat-np-btn-${o.code}`}
                    className="flex-1 h-9 rounded-lg border border-neutral-200 text-neutral-600 text-xs font-medium
                               flex items-center justify-center gap-1.5">
              <Ban className="w-3.5 h-3.5" /> {inProgress ? "Close call" : "Non-productive"}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NonProductiveDialog({ outlet, beatId, gpsOn, onClose }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/lookups", { params: { category: "visit_np_reason" } })
       .then(({ data }) => setReasons(
         (data.items || []).filter((r) => r.code !== "auto_skipped")));
  }, []);

  const submit = async () => {
    if (!reason) return toast.error("Select a reason");
    if (reason === "other" && !remarks.trim())
      return toast.error("Remarks required for 'other'");
    setBusy(true);
    try {
      const gps = gpsOn ? (await captureGps() || {}) : {};
      const { data: visit } = await api.post("/visits/start", {
        retailer_id: outlet.id,
        beat_id: beatId,
        gps,
        client_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.()
                            || `portal-beat-np-${outlet.id}-${Date.now()}`),
      });
      await api.post(`/visits/${visit.id}/end`, {
        gps,
        outcome: "non_productive",
        non_productive_reason: reason,
        non_productive_remarks: reason === "other" ? remarks : null,
        client_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.()
                            || `portal-beat-np-end-${outlet.id}-${Date.now()}`),
      });
      toast.success(`${outlet.name} marked non-productive`);
      onClose(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "visit_already_in_progress") {
        try {
          const gps = gpsOn ? (await captureGps() || {}) : {};
          await api.post(`/visits/${detail.visit_id}/end`, {
            gps, outcome: "non_productive", non_productive_reason: reason,
            non_productive_remarks: reason === "other" ? remarks : null,
            client_ts: new Date().toISOString(),
            idempotency_key: (crypto.randomUUID?.()
                                || `portal-beat-np-end-${outlet.id}-${Date.now()}`),
          });
          toast.success(`${outlet.name} marked non-productive`);
          onClose(true);
          return;
        } catch (e2) { toast.error(formatApiError(e2)); return; }
      }
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose(false)}>
      <DialogContent className="max-w-sm" data-testid="portal-beat-np-dialog">
        <DialogHeader>
          <DialogTitle>Non-productive — {outlet.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="portal-beat-np-reason-select">
              <SelectValue placeholder="Choose reason" />
            </SelectTrigger>
            <SelectContent>
              {reasons.map((r) => (
                <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {reason === "other" && (
            <Textarea rows={2} placeholder="Remarks (required)"
                      value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      maxLength={500} data-testid="portal-beat-np-remarks" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="tulsi-gradient text-white"
                  data-testid="portal-beat-np-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
